import React, { useId } from "react";

import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { ChevronRight, Close, Warning } from "@gaming/xds-react-js-icons";

import { Modal } from "../../components/Modal/Modal";
import { PartnerAccountIconImage } from "../../components/PartnerAccountIconImage";
import * as styles from "./InvitationConfirmationModal.module.css";

type InvitationConfirmationModalProps = Readonly<{
  isOpen: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  transfer?: Readonly<{
    sourceAccountId: string;
    sourceName: string;
    targetAccountId: string;
    targetName: string;
    invitedBy: string;
    invitedByLabel: string;
    warning: string;
  }>;
  onConfirm: () => void;
  onCancel: () => void;
}>;

export const InvitationConfirmationModal = ({
  isOpen,
  title,
  description,
  confirmLabel,
  cancelLabel,
  transfer,
  onConfirm,
  onCancel,
}: InvitationConfirmationModalProps) => {
  const titleId = useId();

  return (
    <Modal isOpen={isOpen} onClose={onCancel} ariaLabelledBy={titleId}>
      <div className={`${styles.modalBody} ${transfer ? styles.transferModalBody : ""}`}>
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.close}
            aria-label={cancelLabel}
            onClick={onCancel}
          >
            <Icon aria-hidden="true" icon={Close} iconSize={IconSize.Custom} />
          </button>
        </div>
        <p className={styles.description}>{description}</p>
        {transfer && (
          <>
            <div className={styles.transfer}>
              <div className={`${styles.organizationCard} ${styles.sourceCard}`}>
                <span className={styles.organizationImage}>
                  <PartnerAccountIconImage accountId={transfer.sourceAccountId} />
                </span>
                <span className={styles.sourceName}>{transfer.sourceName}</span>
              </div>
              <Icon
                aria-hidden="true"
                className={styles.transferArrow}
                icon={ChevronRight}
                iconSize={IconSize.Custom}
              />
              <div className={`${styles.organizationCard} ${styles.targetCard}`}>
                <span className={styles.organizationImage}>
                  <PartnerAccountIconImage accountId={transfer.targetAccountId} />
                </span>
                <span className={styles.organizationDetails}>
                  <strong>{transfer.targetName}</strong>
                  <span>
                    {transfer.invitedByLabel} {transfer.invitedBy}
                  </span>
                </span>
              </div>
            </div>
            <div className={styles.warning} role="alert">
              <Icon
                aria-hidden="true"
                className={styles.warningIcon}
                icon={Warning}
                iconSize={IconSize.Custom}
              />
              {transfer.warning}
            </div>
          </>
        )}
        <div className={styles.buttonRow}>
          <BasicButton
            as="button"
            buttonType={ButtonType.Secondary}
            className={styles.button}
            type="button"
            onClick={onCancel}
          >
            {cancelLabel}
          </BasicButton>
          <BasicButton
            as="button"
            buttonType={ButtonType.Brand}
            className={styles.button}
            type="button"
            onClick={onConfirm}
          >
            {confirmLabel}
          </BasicButton>
        </div>
      </div>
    </Modal>
  );
};
